import {
    MapStore,
    Store,
    WebCacheStore,
    WebStorageStore,
} from '@phi/util/store';
import init_settings, { Settings } from './Settings';
import init_device_storage, { DeviceStorage } from './deviceStorage';
import state, { State } from './state';
import { months, nice_version_format } from '@phi/util';
import { version } from '@phi/constants';
import { Background } from './Colours';
import { ref } from 'vue';
import { AppURLParams } from './util/parseParams';
import { frontend_service_container } from '@phi/frontend/service-container';
import { add1 } from '@vaaas/fpts/set';
import { SessionType } from '@phi/authentication/frontend';

export const initialised = ref(false);

/** Exposes private application data globally as a singleton for legacy compatibility.
 *
 * Keep in mind that importing the application container without initialising it will resolve its fields as undefined.
 */
export type ApplicationContainer = {
    state: State;
    settings: Settings;
    deviceStorage: DeviceStorage;
}

/** initialise the application container
 *
 * this must be done before any access to the application container in order not to cause errors
 */
export async function make_application_container(): Promise<ApplicationContainer> {
    /* depending on user paranoia, any of these may fail to initialise
     * because the user has disabled third-party cookies
     *
     * this can only happen inside an Onshape iframe context
     * we keep trying increasingly supported methods until one sticks
     */
    if (initialised.value) return application_container;
    const store: Store = await caches.open('phi')
        .then(x => new WebCacheStore(x))
        .catch(() => new WebStorageStore(localStorage))
        .catch(() => new MapStore());
    application_container.settings = await init_settings(store);
    application_container.deviceStorage = await init_device_storage(store);
    application_container.state = state;
    return application_container;
}

const application_container = {} as ApplicationContainer;

export default application_container;

export function should_show_cookie_notice(now: Date): boolean {
    const c = application_container;
    if (c.state.framework !== undefined)
        return false;
    else if (c.deviceStorage.consents_to_cookies === false)
        return false;
    else if (c.deviceStorage.consents_to_cookies === undefined)
        return true;
    else if (
        c.deviceStorage.consents_to_cookies === true
        && now.getTime() > (c.deviceStorage.last_consented_to_cookies ?? 0) + months(6)
    )
        return true;
    else
        return false;
}

export function accept_or_reject_cookies(accept: boolean) {
    const c = application_container;
    c.deviceStorage.consents_to_cookies = accept;
    c.deviceStorage.last_consented_to_cookies = Date.now();
}

export function update_skipped_tutorial() {
    application_container.settings.skip_tutorial = nice_version_format(version);
}

export function should_skip_tutorial() {
    return application_container.settings.skip_tutorial === nice_version_format(version);
}

export function white_mode() {
    const app = application_container;
    return initialised.value === true
        && app.settings.background === Background.white
        && !app.settings.inset;
}

export function handle_login_params(params: AppURLParams) {
    const app = application_container;
    const service = frontend_service_container();

    console.log("P5:")
    console.log(params)

    app.state.framework = params.server && params.server.includes('onshape')
        ? 'onshape'
        : undefined;
    app.state.workspace = params.workspaceId ? params.workspaceId : undefined;
    app.state.document = params.documentId;
    app.state.element = params.elementId;
    app.state.onshape_server = params.server ?? app.state.onshape_server;

    if (params.token && params.token !== app.deviceStorage.token)
        app.deviceStorage.token = params.token;

    if (params.subscription && params.subscription !== app.deviceStorage.user_subscription_status)
        app.deviceStorage.user_subscription_status = params.subscription;

    console.log("P6:")
    console.log(params.expires)

    if (params.expires)
        app.deviceStorage.user_trial_end = parseFloat(params.expires);

    if (params.hasOwnProperty('onshape')) {
        service.authentication.session_type.value = add1('onshape' as SessionType)(service.authentication.session_type.value);
        app.state.framework = 'onshape';
    }

    if (params.hasOwnProperty('standalone'))
        service.authentication.session_type.value = add1('onshape' as SessionType)(service.authentication.session_type.value);

    if (params.hasOwnProperty('demo')) {
        service.authentication.session_type.value = add1('demo' as SessionType)(service.authentication.session_type.value);
        app.state.is_demo = true;
    }

    app.state.warning = params.warning;

    if (params.expires)
        console.log(`Your subscription expires in ${params.expires} days`);
}
